import axios from 'axios';

// Get backend URL from environment
const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests if available
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle responses and token expiration
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Requests can opt out of the global login redirect (e.g. the
      // public purchase card handles 401 with its own signup guidance).
      if (error.config?.skipAuthRedirect) {
        return Promise.reject(error);
      }
      // Token expired or invalid, clear it
      localStorage.removeItem('access_token');
      localStorage.removeItem('user_data');
      // Redirect to login if not already there
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Authentication API - simplified to use correct endpoint
export const authAPI = {
  signup: async (userData) => {
    try {
      const response = await api.post('/api/auth/signup', userData);
      return response.data;
    } catch (error) {
      console.error('Signup failed:', error.response?.data || error.message);
      throw new Error(error.response?.data?.detail || 'Signup failed - please try again');
    }
  },

  login: async (credentials) => {
    try {
      const response = await api.post('/api/auth/login', credentials);
      const { access_token } = response.data;
      
      // Store token
      localStorage.setItem('access_token', access_token);
      
      // Get user data and store it
      const userResponse = await api.get('/api/me');
      localStorage.setItem('user_data', JSON.stringify(userResponse.data));
      
      return response.data;
    } catch (error) {
      console.error('Login failed:', error.response?.data || error.message);
      throw new Error(error.response?.data?.detail || 'Login failed - please check your credentials');
    }
  },

  logout: () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('user_data');
  },

  getCurrentUser: async () => {
    try {
      const response = await api.get('/api/me');
      return response.data;
    } catch (error) {
      console.error('Get current user failed:', error.response?.data || error.message);
      throw new Error('Failed to get current user');
    }
  }
};

// Profile API - simplified to use correct endpoint
export const profileAPI = {
  getProfile: async () => {
    try {
      const response = await api.get('/api/profile');
      return response.data;
    } catch (error) {
      console.error('Get profile failed:', error.response?.data || error.message);
      throw new Error('Failed to get profile');
    }
  },

  updateProfile: async (profileData) => {
    try {
      const response = await api.put('/api/profile', profileData);
      return response.data;
    } catch (error) {
      console.error('Update profile failed:', error.response?.data || error.message);
      throw new Error('Failed to update profile');
    }
  },

  // Iter-26 Phase 1: Patch ONLY the supplied cascading / identity-
  // locked fields onto the master profile. Called by the 3-way sync
  // modal in Resume Builder / Cover Letter Generator when the
  // candidate picks "Update profile and all documents". Anything not
  // included in ``patch`` is left untouched.
  syncFromDocument: async (patch) => {
    const response = await api.post('/api/profile/sync', patch);
    return response.data;
  },

  uploadPhoto: async (file, original) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      if (original) formData.append('original', original);
      
      const response = await api.post('/api/profile/upload-photo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return response.data;
    } catch (error) {
      console.error('Upload photo failed:', error.response?.data || error.message);
      throw new Error(error.response?.data?.detail || 'Failed to upload photo');
    }
  },

  deletePhoto: async () => {
    const response = await api.delete('/api/profile/photo');
    return response.data;
  },

  uploadSignature: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post('/api/profile/upload-signature', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  deleteSignature: async () => {
    const response = await api.delete('/api/profile/signature');
    return response.data;
  }
};

// Resolve a relative ``/api/media/...`` URL or a data: URL into an
// absolute browser-loadable URL. Pass through any string that's already
// absolute (http/https or data:) unchanged.
export const resolveAssetUrl = (url) => {
  if (!url || typeof url !== 'string') return '';
  if (url.startsWith('data:') || url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }
  if (url.startsWith('/')) {
    return `${API_BASE_URL}${url}`;
  }
  return url;
};

// Dashboard API - simplified to use correct endpoint
export const dashboardAPI = {
  getStats: async () => {
    try {
      const response = await api.get('/api/dashboard/stats');
      return response.data;
    } catch (error) {
      console.error('Get dashboard stats failed:', error.response?.data || error.message);
      throw new Error('Failed to get dashboard stats');
    }
  }
};

// Resume API - for resume management
export const resumeAPI = {
  createResume: async (resumeData) => {
    try {
      const response = await api.post('/api/resume', resumeData);
      return response.data;
    } catch (error) {
      console.error('Create resume failed:', error.response?.data || error.message);
      throw new Error(error.response?.data?.detail || 'Failed to create resume');
    }
  },

  getResume: async () => {
    try {
      const response = await api.get('/api/resume');
      return response.data;
    } catch (error) {
      if (error.response?.status === 404) {
        return null; // No resume found
      }
      console.error('Get resume failed:', error.response?.data || error.message);
      throw new Error('Failed to get resume');
    }
  },

  updateResume: async (resumeData) => {
    try {
      const response = await api.put('/api/resume', resumeData);
      return response.data;
    } catch (error) {
      console.error('Update resume failed:', error.response?.data || error.message);
      throw new Error('Failed to update resume');
    }
  },

  deleteResume: async () => {
    try {
      const response = await api.delete('/api/resume');
      return response.data;
    } catch (error) {
      console.error('Delete resume failed:', error.response?.data || error.message);
      throw new Error('Failed to delete resume');
    }
  },

  getResumeById: async (resumeId) => {
    try {
      const response = await api.get(`/api/resumes/${resumeId}`);
      return response.data;
    } catch (error) {
      if (error.response?.status === 404) {
        return null; // Resume not found
      }
      console.error('Get resume by ID failed:', error.response?.data || error.message);
      throw new Error('Failed to get resume');
    }
  },

  // Render the in-progress resume payload to a PDF blob (live preview).
  previewPDF: async (resumeData) => {
    const response = await api.post('/api/resume/preview-pdf', resumeData, {
      responseType: 'blob',
    });
    return response.data;
  },

  // Download the saved resume as a polished branded PDF.
  downloadPDF: async () => {
    const response = await api.get('/api/resume/pdf', {
      responseType: 'blob',
    });
    return response.data;
  }
};

// Cover Letter API - for cover letter management
export const coverLetterAPI = {
  createCoverLetter: async (coverLetterData) => {
    try {
      const response = await api.post('/api/cover-letter', coverLetterData);
      return response.data;
    } catch (error) {
      console.error('Create cover letter failed:', error.response?.data || error.message);
      throw new Error(error.response?.data?.detail || 'Failed to create cover letter');
    }
  },

  getCoverLetter: async () => {
    try {
      const response = await api.get('/api/cover-letter');
      return response.data;
    } catch (error) {
      if (error.response?.status === 404) {
        return null; // No cover letter found
      }
      console.error('Get cover letter failed:', error.response?.data || error.message);
      throw new Error('Failed to get cover letter');
    }
  },

  updateCoverLetter: async (coverLetterData) => {
    try {
      const response = await api.put('/api/cover-letter', coverLetterData);
      return response.data;
    } catch (error) {
      console.error('Update cover letter failed:', error.response?.data || error.message);
      throw new Error('Failed to update cover letter');
    }
  },

  deleteCoverLetter: async () => {
    try {
      const response = await api.delete('/api/cover-letter');
      return response.data;
    } catch (error) {
      console.error('Delete cover letter failed:', error.response?.data || error.message);
      throw new Error('Failed to delete cover letter');
    }
  },

  getCoverLetterById: async (coverLetterId) => {
    try {
      const response = await api.get(`/api/cover-letters/${coverLetterId}`);
      return response.data;
    } catch (error) {
      if (error.response?.status === 404) {
        return null; // Cover letter not found
      }
      console.error('Get cover letter by ID failed:', error.response?.data || error.message);
      throw new Error('Failed to get cover letter');
    }
  },

  generatePDF: async (pdfData) => {
    try {
      const response = await api.post('/api/cover-letter/generate-pdf', pdfData, {
        responseType: 'blob',
      });
      return response.data;
    } catch (error) {
      console.error('Generate PDF failed:', error.response?.data || error.message);
      throw new Error('Failed to generate PDF');
    }
  },

  // Render the cover letter (page 1) + saved resume (page 2+) as a
  // single combined Application Package PDF — used both for live
  // preview and the "Download Application Package" CTA.
  // Iter-37.6: `includeSuccessAgreement` (default false) optionally
  // appends the candidate's currently active Success Agreement as a
  // bonus add-on. OFF by default — the standard package is CL + Resume.
  previewApplicationPackage: async (pdfData, { includeSuccessAgreement = false } = {}) => {
    const response = await api.post('/api/application-package/preview-pdf', pdfData, {
      params: includeSuccessAgreement ? { include_success_agreement: true } : undefined,
      responseType: 'blob',
    });
    return response.data;
  },

  downloadApplicationPackage: async ({ includeSuccessAgreement = false } = {}) => {
    const response = await api.get('/api/application-package/pdf', {
      params: includeSuccessAgreement ? { include_success_agreement: true } : undefined,
      responseType: 'blob',
    });
    return response.data;
  }
};

// QR Code API - for QR code management
export const qrCodeAPI = {
  createQRCode: async (qrData = {}) => {
    try {
      const response = await api.post('/api/qr-code', qrData);
      return response.data;
    } catch (error) {
      console.error('Create QR code failed:', error.response?.data || error.message);
      throw new Error(error.response?.data?.detail || 'Failed to create QR code');
    }
  },

  getQRCodes: async () => {
    try {
      const response = await api.get('/api/qr-code');
      return response.data;
    } catch (error) {
      console.error('Get QR codes failed:', error.response?.data || error.message);
      throw new Error('Failed to get QR codes');
    }
  },

  getQRCodeById: async (qrId) => {
    try {
      const response = await api.get(`/api/qr-code/${qrId}`);
      return response.data;
    } catch (error) {
      if (error.response?.status === 404) {
        return null; // QR code not found
      }
      console.error('Get QR code by ID failed:', error.response?.data || error.message);
      throw new Error('Failed to get QR code');
    }
  },

  deleteQRCode: async (qrId) => {
    try {
      const response = await api.delete(`/api/qr-code/${qrId}`);
      return response.data;
    } catch (error) {
      console.error('Delete QR code failed:', error.response?.data || error.message);
      throw new Error('Failed to delete QR code');
    }
  },

  // Iter-40 — canonical "QR & Public Profile" status endpoint.
  getStatus: async () => {
    const response = await api.get('/api/qr-code/status');
    return response.data;
  },
};

// Utility functions
export const isAuthenticated = () => {
  const token = localStorage.getItem('access_token');
  const userData = localStorage.getItem('user_data');
  return !!(token && userData);
};

export const getUserData = () => {
  const userData = localStorage.getItem('user_data');
  return userData ? JSON.parse(userData) : null;
};

// Video Interview API - for video interview management
export const videoInterviewAPI = {
  createVideoInterview: async (videoData) => {
    try {
      const response = await api.post('/api/video-interview', videoData);
      return response.data;
    } catch (error) {
      console.error('Create video interview failed:', error.response?.data || error.message);
      throw new Error(error.response?.data?.detail || 'Failed to create video interview');
    }
  },

  getVideoInterview: async () => {
    try {
      const response = await api.get('/api/video-interview');
      return response.data;
    } catch (error) {
      if (error.response?.status === 404) {
        return null; // No video interview found
      }
      console.error('Get video interview failed:', error.response?.data || error.message);
      throw new Error('Failed to get video interview');
    }
  },

  updateVideoInterview: async (videoData) => {
    try {
      const response = await api.put('/api/video-interview', videoData);
      return response.data;
    } catch (error) {
      console.error('Update video interview failed:', error.response?.data || error.message);
      throw new Error('Failed to update video interview');
    }
  },

  deleteVideoInterview: async () => {
    try {
      const response = await api.delete('/api/video-interview');
      return response.data;
    } catch (error) {
      console.error('Delete video interview failed:', error.response?.data || error.message);
      throw new Error('Failed to delete video interview');
    }
  },

  getVideoInterviewById: async (videoId) => {
    try {
      const response = await api.get(`/api/video-interview/${videoId}`);
      return response.data;
    } catch (error) {
      if (error.response?.status === 404) {
        return null; // Video interview not found
      }
      console.error('Get video interview by ID failed:', error.response?.data || error.message);
      throw new Error('Failed to get video interview');
    }
  },

  uploadVideo: async (file, onProgress) => {
    try {
      // Validate file type and size on frontend
      if (!file.type.startsWith('video/')) {
        throw new Error('Please select a video file');
      }
      
      if (file.type !== 'video/mp4') {
        throw new Error('Only MP4 format is supported');
      }

      const maxSize = 200 * 1024 * 1024; // 200MB
      if (file.size > maxSize) {
        throw new Error('File size must be less than 200MB');
      }

      const formData = new FormData();
      formData.append('file', file);
      
      const response = await api.post('/api/video-interview/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          if (onProgress) {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            onProgress(percentCompleted);
          }
        }
      });
      return response.data;
    } catch (error) {
      console.error('Upload video failed:', error.response?.data || error.message);
      throw new Error(error.response?.data?.detail || error.message || 'Failed to upload video');
    }
  },

  // Guided 5-question interview flow
  getGuidedInterview: async () => {
    const response = await api.get('/api/video-interview/guided');
    return response.data;
  },

  startGuidedInterview: async () => {
    const response = await api.post('/api/video-interview/guided/start');
    return response.data;
  },

  uploadGuidedSegment: async (blob, questionIndex, durationSeconds, onProgress, onAttempt) => {
    const ext = blob.type.includes('mp4') ? 'mp4' : 'webm';
    const params = new URLSearchParams({
      question_index: String(questionIndex),
      duration_seconds: String(Math.max(0, Number(durationSeconds) || 0)),
    });
    const url = `/api/video-interview/guided/segment?${params.toString()}`;

    const MAX_ATTEMPTS = 3;
    let lastError;

    for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
      // Build a fresh FormData each attempt (stream is consumed on failure)
      const formData = new FormData();
      formData.append('file', blob, `q${questionIndex}.${ext}`);

      if (onAttempt) onAttempt(attempt, MAX_ATTEMPTS);
      try {
        // IMPORTANT: explicitly set Content-Type to 'multipart/form-data' so
        // it overrides the axios *default* JSON header set on the shared
        // instance.  When axios sees a FormData body, it auto-replaces this
        // with the proper 'multipart/form-data; boundary=...' string.
        // (Without this, the default application/json leaks through and the
        // server rejects the upload with a 422 validation error array.)
        const response = await api.post(url, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          onUploadProgress: (e) => {
            if (onProgress && e.total) {
              onProgress(Math.round((e.loaded * 100) / e.total));
            }
          },
        });
        return response.data;
      } catch (err) {
        lastError = err;
        // Surface the exact failing payload so we can reason about future
        // failures without needing to re-instrument production.
        try {
          console.error("[uploadGuidedSegment] attempt", attempt, "failed", {
            url,
            blobSize: blob && blob.size,
            blobType: blob && blob.type,
            status: err?.response?.status,
            data: err?.response?.data,
          });
        } catch (_) {
          /* ignore log failures */
        }
        const status = err?.response?.status;
        const isTransient =
          !err?.response || // network/timeout/CORS — no response at all
          (status >= 500 && status < 600) ||
          status === 408 ||
          status === 429;
        if (!isTransient || attempt === MAX_ATTEMPTS) {
          throw err;
        }
        // Exponential backoff: 1s, 2s
        const waitMs = 1000 * Math.pow(2, attempt - 1);
        await new Promise((resolve) => setTimeout(resolve, waitMs));
      }
    }
    throw lastError;
  },

  deleteGuidedSegment: async (questionIndex) => {
    const response = await api.delete(`/api/video-interview/guided/segment/${questionIndex}`);
    return response.data;
  },

  finalizeGuidedInterview: async () => {
    const response = await api.post('/api/video-interview/guided/finalize');
    return response.data;
  },

  setPresentationMode: async (mode) => {
    const response = await api.post('/api/video-interview/presentation-mode', {
      presentation_mode: mode,
    });
    return response.data;
  },
};

// ─── Iter-36: Intros (draft/published) + Application Packages ──────────────
export const introsAPI = {
  getState: async () => {
    const r = await api.get('/api/intros');
    return r.data;
  },
  createDraft: async (mode) => {
    const r = await api.post('/api/intros/draft', { mode });
    return r.data.draft;
  },
  discardDraft: async (mode) => {
    const r = await api.delete('/api/intros/draft', { params: { mode } });
    return r.data;
  },
  uploadSegment: async (blob, mode, questionIndex, durationSeconds, onProgress, onAttempt) => {
    const isAudio = mode === 'audio';
    const ext = isAudio ? 'webm' : (blob.type.includes('mp4') ? 'mp4' : 'webm');
    const params = new URLSearchParams({
      mode,
      question_index: String(questionIndex),
      duration_seconds: String(Math.max(0, Number(durationSeconds) || 0)),
    });
    const url = `/api/intros/draft/segment?${params.toString()}`;
    const MAX = 3;
    let last;
    for (let attempt = 1; attempt <= MAX; attempt += 1) {
      const form = new FormData();
      form.append('file', blob, `q${questionIndex}.${ext}`);
      if (onAttempt) onAttempt(attempt, MAX);
      try {
        const r = await api.post(url, form, {
          headers: { 'Content-Type': 'multipart/form-data' },
          onUploadProgress: (e) => {
            if (onProgress && e.total) onProgress(Math.round((e.loaded * 100) / e.total));
          },
        });
        return r.data;
      } catch (err) {
        last = err;
        const status = err?.response?.status;
        const transient = !err?.response || (status >= 500 && status < 600) || status === 408 || status === 429;
        if (!transient || attempt === MAX) throw err;
        await new Promise((res) => setTimeout(res, 1000 * 2 ** (attempt - 1)));
      }
    }
    throw last;
  },
  deleteSegment: async (mode, questionIndex) => {
    const r = await api.delete(`/api/intros/draft/segment/${questionIndex}`, { params: { mode } });
    return r.data;
  },
  finalizeDraft: async (mode) => {
    const r = await api.post('/api/intros/draft/finalize', null, { params: { mode } });
    return r.data;
  },
  markPreviewSeen: async (introId) => {
    const r = await api.post(`/api/intros/${introId}/preview-seen`);
    return r.data;
  },
  publish: async (introId) => {
    const r = await api.post(`/api/intros/${introId}/publish`);
    return r.data;
  },
  setPresentationMode: async (mode) => {
    const r = await api.post('/api/intros/presentation-mode', { presentation_mode: mode });
    return r.data;
  },
  listPackages: async () => {
    const r = await api.get('/api/application-packages');
    return r.data.packages;
  },
  reprintPackage: async (packageId) => {
    const r = await api.get(`/api/application-package/reprint/${packageId}`, {
      responseType: 'blob',
    });
    return r.data;
  },
  // Iter-40 Phase 4.2 — convenience path: derive an audio draft from
  // the candidate's currently published video intro. Server runs
  // ffmpeg, creates a state="draft" status="ready_to_publish" row,
  // and returns the refreshed intros state so the caller can swap
  // straight to the audio tab.
  deriveAudioFromVideo: async () => {
    const r = await api.post('/api/intros/derive-audio-from-video');
    return r.data;
  },
  // Iter-40 Phase 4.3 — upload an existing intro file (video or audio
  // the candidate already produced). Server enforces strict MIME +
  // size + duration whitelists and lands the result as a
  // state="draft" status="ready_to_publish" row so the existing
  // Preview & Publish flow handles the rest.
  uploadIntro: async (mode, file) => {
    const form = new FormData();
    form.append('file', file);
    const r = await api.post(`/api/intros/upload?mode=${encodeURIComponent(mode)}`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      // Uploads can take a moment; bump the per-call timeout.
      timeout: 120000,
    });
    return r.data;
  },
  // Iter-40 Phase 4.1 — fetch an owner-only download blob for a
  // published intro. Returns a Blob; the caller wires up the
  // download anchor + nice filename from the Content-Disposition.
  downloadIntro: async (introId) => {
    const r = await api.get(`/api/intros/${introId}/download`, {
      responseType: 'blob',
    });
    // Parse filename from Content-Disposition (best-effort).
    const cd = r.headers['content-disposition'] || r.headers['Content-Disposition'] || '';
    const m = cd.match(/filename="?([^"]+)"?/i);
    return { blob: r.data, filename: m ? m[1] : `inter-view-intro-${introId}` };
  },
};

// ─── Iter-37: Success Agreement ────────────────────────────────────────────
export const successAgreementAPI = {
  getDefaults: async (termYears = 1.0) => {
    const r = await api.get('/api/success-agreement/defaults', { params: { term_years: termYears } });
    return r.data;
  },
  getState: async () => {
    const r = await api.get('/api/success-agreement');
    return r.data; // {active, draft}
  },
  list: async () => {
    const r = await api.get('/api/success-agreements');
    return r.data.agreements;
  },
  saveDraft: async (data) => {
    const r = await api.post('/api/success-agreement', data);
    return r.data.agreement;
  },
  update: async (id, data) => {
    const r = await api.put(`/api/success-agreement/${id}`, data);
    return r.data.agreement;
  },
  discard: async (id) => {
    const r = await api.delete(`/api/success-agreement/${id}`);
    return r.data;
  },
  activate: async (id) => {
    const r = await api.post(`/api/success-agreement/${id}/activate`);
    return r.data.agreement;
  },
  downloadPdf: async (id) => {
    const r = await api.get(`/api/success-agreement/${id}/pdf`, { responseType: 'blob' });
    return r.data;
  },
  previewPdf: async (data) => {
    const r = await api.post('/api/success-agreement/preview-pdf', data, { responseType: 'blob' });
    return r.data;
  },
};

// ─────────────────────────────────────────────────────────────────
// Inter-View Employer Experience — Phase 1A
// Public (unauthenticated) inquiry entry point + status lookup.
// ─────────────────────────────────────────────────────────────────
export const employerAPI = {
  submitInquiry: async (payload) => {
    const r = await api.post('/api/employer/intake/inquiry', payload);
    return r.data;
  },
  getInquiryByRef: async (serviceReference) => {
    const r = await api.get(
      `/api/employer/intake/inquiry/by-ref/${encodeURIComponent(serviceReference)}`
    );
    return r.data;
  },
  // ─── Phase 1B — Direct purchase (Stripe Checkout) ─────────────────
  //
  // The amount is defined server-side in ``pricing_catalog.py`` and
  // is NOT sent by the client. If the amount hasn't been locked yet
  // (Founder-controlled), the server returns 503 and the caller shows
  // a friendly "not yet available" toast.
  createPurchaseCheckout: async (payload) => {
    const r = await api.post('/api/employer/purchase/checkout', payload, { skipAuthRedirect: true });
    return r.data; // { checkout_url, session_id, service_reference, amount_cents, currency }
  },
  getPaymentStatus: async (sessionId) => {
    const r = await api.get(
      `/api/payments/status/${encodeURIComponent(sessionId)}`
    );
    return r.data; // { session_id, status, payment_status, service_reference }
  },
};

export default api;